import React from "react";
import { ShoppingBag, ExternalLink } from "lucide-react";

const CATEGORY_DE = {
  "anti-hairloss": "Anti-Haarausfall",
  "hair-repair": "Haarreparatur",
  "colour-care": "Farbpflege",
  "hair-follicle-strength": "Haarfollikel-Stärkung",
  "hair-growth-and-repair": "Haarwachstum & Reparatur",
  "hair-growth": "Haarwachstum",
};

export default function ProductCard({ product, onAdd, showMatch = false, testIdPrefix = "product" }) {
  const {
    id,
    name,
    category,
    price,
    original_price,
    discount_percent,
    image_url,
    match_score,
    reason,
    product_url,
  } = product;

  const handleClick = () => {
    if (product_url) {
      window.open(product_url, "_blank", "noopener,noreferrer");
    } else {
      onAdd?.(product);
    }
  };

  return (
    <article
      data-testid={`${testIdPrefix}-card-${id}`}
      className="group relative bg-white border hairline flex flex-col overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-24px_rgba(31,26,23,0.35)]"
    >
      <a
        href={product_url || undefined}
        target={product_url ? "_blank" : undefined}
        rel={product_url ? "noopener noreferrer" : undefined}
        className="relative aspect-square overflow-hidden bg-[var(--brand-bg-2)] block"
      >
        {discount_percent ? (
          <span className="absolute top-2 left-2 sm:top-3 sm:left-3 z-10 text-[10px] sm:text-[11px] font-medium tracking-widest uppercase text-white bg-[var(--brand-gold)] px-2 py-0.5 sm:px-2.5 sm:py-1">
            −{discount_percent}%
          </span>
        ) : null}
        {showMatch && typeof match_score === "number" && (
          <span
            data-testid={`${testIdPrefix}-match-${id}`}
            className="absolute top-2 right-2 sm:top-3 sm:right-3 z-10 text-[10px] sm:text-[11px] font-medium tracking-widest uppercase bg-white text-[var(--brand-ink)] border hairline px-2 py-0.5 sm:px-2.5 sm:py-1"
          >
            {match_score}% Match
          </span>
        )}
        <img
          src={image_url}
          alt={name}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </a>

      <div className="flex-1 p-3 sm:p-5 flex flex-col">
        <p className="text-[9px] sm:text-[11px] tracking-[0.12em] sm:tracking-[0.2em] uppercase text-[var(--brand-gold)] leading-tight break-words">
          {CATEGORY_DE[String(category).toLowerCase()] || category}
        </p>
        <h3
          className="mt-1.5 sm:mt-2 font-serif-display text-sm sm:text-lg lg:text-xl leading-snug text-[var(--brand-ink)] break-words"
          title={name}
        >
          {name}
        </h3>

        {showMatch && reason && (
          <p className="mt-2 text-[11px] italic text-[var(--brand-ink-soft)] border-l-2 border-[var(--brand-gold)] pl-3 break-words">
            {reason}
          </p>
        )}

        <div className="mt-auto pt-3 sm:pt-4">
          <div className="flex items-baseline gap-1.5 flex-wrap">
            <span className="font-serif-display text-base sm:text-xl lg:text-2xl text-[var(--brand-gold)] tabular-nums">
              €{price.toFixed(2)}
            </span>
            {original_price > price && (
              <span className="text-[11px] sm:text-sm text-[var(--brand-ink-soft)] line-through tabular-nums">
                €{original_price.toFixed(2)}
              </span>
            )}
          </div>

          <button
            type="button"
            onClick={handleClick}
            data-testid={`${testIdPrefix}-add-${id}`}
            className="mt-2.5 sm:mt-4 w-full btn-gold inline-flex items-center justify-center gap-1.5 px-2 sm:px-4 py-2.5 sm:py-3 text-[10px] sm:text-xs font-medium uppercase tracking-[0.12em] sm:tracking-[0.2em] whitespace-nowrap"
          >
            {product_url ? (
              <>
                <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={1.5} />
                Bei Eternl kaufen
              </>
            ) : (
              <>
                <ShoppingBag className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={1.5} />
                In den Warenkorb
              </>
            )}
          </button>
        </div>
      </div>
    </article>
  );
}
